// Dubai - AC not cooling (problem page).
const content = {
    slug: 'ac-not-cooling',
    title: 'AC Not Cooling in Dubai? Causes, Fixes and Costs | StringsSG',
    description: (c) =>
        `Why your AC is not cooling in Dubai, what to check first, and what each fix costs - from a ${c.money(c.tables.general[0].wall)} cleaning to a gas refill or repair.`,
    eyebrow: 'Dubai · AC not cooling',
    h1: 'AC Not Cooling in Dubai?',
    lead: 'At 45°C outside, an AC that only manages 27°C inside feels broken - but most of the time it just needs cleaning. Here is how to tell what is wrong, and what each fix costs.',
    image: 'blog/living/cool-air-from-aircon.jpg',
    imageAlt: 'Checking the air coming out of an AC that is not cooling properly',
    facts: ['Check these 4 things first', 'Most fixes are cleaning', 'Diagnosis if unsure'],
    ctaLabel: 'See the fixes and prices',
    priceTitle: 'The two fixes that solve most cases',
    priceIntro: 'A dirty coil causes more "not cooling" calls in Dubai than anything else. Start with a cleaning or a coil wash; if the technician finds a deeper problem, they tell you before doing anything else.',
    panel: { type: 'servicing', services: ['chemicalWash', 'general'], defaultMount: 'Ducted' },
    sections: [
        {
            title: 'Check these four things before you book',
            steps: [
                { title: 'Thermostat mode and setting', body: 'Make sure it is on Cool (not Fan or Dry) and set below room temperature. A setting of 24°C is realistic in summer; 18°C will not cool faster, it just runs longer.' },
                { title: 'The filter', body: 'Open the return grille and look. A grey, matted filter can halve the airflow. Wash it and try again after an hour.' },
                { title: 'The outdoor unit (split systems)', body: 'Is the fan spinning? Is the coil covered in sand or blocked by stored boxes? It needs space to throw heat out.' },
                { title: 'The breaker and the time of day', body: 'A tripped breaker stops the outdoor unit while the indoor fan still runs. And at 3 pm in August every unit in Dubai struggles - judge it in the evening.' },
            ],
        },
        {
            title: 'What the cause usually turns out to be',
            tone: 'muted',
            cards: [
                { title: 'Dirty coil (most common)', body: 'Air comes out cool but weak. Fixed by a coil chemical wash.' },
                { title: 'Low refrigerant', body: 'Air is barely cool, with frost on the pipes outside. Needs a leak check and gas refill.' },
                { title: 'Failed capacitor', body: 'The outdoor fan or compressor hums but does not start. A small part, quickly replaced.' },
                { title: 'Undersized unit', body: 'A 1-ton unit in a sun-facing living room will lose in July. See our AC ton size guide.' },
            ],
        },
        {
            title: 'District-cooled apartment?',
            paragraphs: [
                'If you pay a chiller or cooling bill separately from DEWA, your building is on district cooling. Your unit has no compressor or gas, so the fixes are different: clean the filter and coil, check the thermostat, and if the supply air is not cold at all, ask building management whether the chilled-water valve on your unit is working.',
            ],
        },
    ],
    faqs: [
        {
            question: 'Why is my AC running but not cooling in Dubai?',
            answer: 'Most often a dirty filter or coil restricting airflow, then low refrigerant from a leak, then a failed capacitor stopping the outdoor unit. A cleaning fixes the first; the other two need a technician.',
        },
        {
            question: 'Should I just refill the gas?',
            answer: 'Only if a pressure test shows it is low. Refilling a unit that is not low on gas does nothing, and refilling a leaking one only lasts weeks.',
        },
        {
            question: 'How much does it cost to fix an AC that is not cooling?',
            answer: (c) => `A cleaning costs ${c.money(c.tables.general[0].wall)}, a coil wash ${c.money(c.tables.chemicalWash[0].wall)}, and a gas top-up ${c.money(c.tables.gas.R410A.half)} for one split unit. If you are not sure which, a diagnosis visit costs ${c.money(c.tables.repairDiagnosis)}.`,
        },
    ],
    schema: {
        name: 'AC Not Cooling - Diagnosis and Repair in Dubai',
        serviceType: 'Air conditioner troubleshooting',
        offers: (c) => [
            { name: 'AC cleaning, 1 split unit', price: c.tables.general[0].wall },
            { name: 'Coil chemical wash, 1 split unit', price: c.tables.chemicalWash[0].wall },
            { name: 'Repair diagnosis visit', price: c.tables.repairDiagnosis },
        ],
    },
}

export default content
